import React, { useEffect, useRef, useState } from 'react';
import {
    DEAL_TYPES,
    LEAD_SOURCES,
    dealTypeLabel,
    leadSourceLabel,
    stageLabel,
} from '../utils/salesPipeline';

const PRIORITIES = ['High', 'Medium', 'Low'];

/** One-row pipeline toolbar. The dropdowns live in a popover to keep it one line, but every active
 *  filter is repeated as a removable chip, so a filtered list never looks like an empty one. */
export default function SalesToolbar({ filters, onChange, onReset, owners = [], viewMode, onViewMode }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    // Close on outside click or Escape, the way the rest of the app's overlays behave.
    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const set = (key) => (e) => onChange(key, e.target.value);

    // Search stays in the row itself, so it is not counted as a hidden filter.
    const chips = [
        filters.stage && { key: 'stage', label: stageLabel(filters.stage) },
        filters.type && { key: 'type', label: dealTypeLabel(filters.type) },
        filters.source && { key: 'source', label: leadSourceLabel(filters.source) },
        filters.priority && { key: 'priority', label: `Priorität ${filters.priority}` },
        filters.owner && {
            key: 'owner',
            label: owners.find(([id]) => id === filters.owner)?.[1] || 'Verantwortlich',
        },
        filters.onlyDue && { key: 'onlyDue', label: 'Fällige Wiedervorlagen' },
    ].filter(Boolean);

    return (
        <>
            <div className="acq-toolbar">
                <div className="acq-search">
                    <svg className="acq-search-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.75" />
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        className="acq-search-input"
                        placeholder="Deal, Firma oder Kontakt suchen…"
                        value={filters.search}
                        onChange={set('search')}
                    />
                    {filters.search && (
                        <button
                            className="acq-search-clear"
                            onClick={() => onChange('search', '')}
                            aria-label="Suche löschen"
                        >
                            ✕
                        </button>
                    )}
                </div>

                <div className="acq-filter-wrap" ref={wrapRef}>
                    <button
                        className={`acq-filter-btn${chips.length ? ' is-active' : ''}`}
                        onClick={() => setOpen(o => !o)}
                        aria-expanded={open}
                        aria-haspopup="dialog"
                    >
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M3 5h14M6 10h8M8.5 15h3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                        </svg>
                        Filter
                        {chips.length > 0 && <span className="acq-filter-count">{chips.length}</span>}
                    </button>

                    {open && (
                        <div className="acq-filter-pop" role="dialog" aria-label="Filter">
                            <label className="acq-pop-field">
                                <span>Phase</span>
                                <select className="input" value={filters.stage} onChange={set('stage')}>
                                    <option value="">Alle Phasen</option>
                                    {/* Stage is also filterable by clicking the funnel above. */}
                                    {['Lead', 'Contacted', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost']
                                        .map(key => <option key={key} value={key}>{stageLabel(key)}</option>)}
                                </select>
                            </label>
                            <label className="acq-pop-field">
                                <span>Typ</span>
                                <select className="input" value={filters.type} onChange={set('type')}>
                                    <option value="">Projekt &amp; Produkt</option>
                                    {DEAL_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                                </select>
                            </label>
                            <label className="acq-pop-field">
                                <span>Quelle</span>
                                <select className="input" value={filters.source} onChange={set('source')}>
                                    <option value="">Alle Quellen</option>
                                    {LEAD_SOURCES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                                </select>
                            </label>
                            <label className="acq-pop-field">
                                <span>Priorität</span>
                                <select className="input" value={filters.priority} onChange={set('priority')}>
                                    <option value="">Alle Prioritäten</option>
                                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </label>
                            {owners.length > 0 && (
                                <label className="acq-pop-field">
                                    <span>Verantwortlich</span>
                                    <select className="input" value={filters.owner} onChange={set('owner')}>
                                        <option value="">Alle Verantwortlichen</option>
                                        {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className="acq-checkbox acq-pop-check">
                                <input
                                    type="checkbox"
                                    checked={filters.onlyDue}
                                    onChange={(e) => onChange('onlyDue', e.target.checked)}
                                />
                                Nur fällige Wiedervorlagen
                            </label>
                            <div className="acq-pop-footer">
                                <button className="button button-secondary" onClick={onReset} disabled={!chips.length && !filters.search}>
                                    Zurücksetzen
                                </button>
                                <button className="button" onClick={() => setOpen(false)}>Fertig</button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="view-toggle">
                    <button
                        className={`view-btn ${viewMode === 'kanban' ? 'view-btn-active' : ''}`}
                        onClick={() => onViewMode('kanban')}
                        title="Kanban"
                    >
                        ⊞ Board
                    </button>
                    <button
                        className={`view-btn ${viewMode === 'table' ? 'view-btn-active' : ''}`}
                        onClick={() => onViewMode('table')}
                        title="Tabelle"
                    >
                        ☰ Liste
                    </button>
                </div>
            </div>

            {chips.length > 0 && (
                <div className="acq-chips">
                    {chips.map(chip => (
                        <button
                            key={chip.key}
                            className="acq-chip"
                            onClick={() => onChange(chip.key, chip.key === 'onlyDue' ? false : '')}
                            title="Filter entfernen"
                        >
                            {chip.label}<span className="acq-chip-x">✕</span>
                        </button>
                    ))}
                    <button className="acq-chip-reset" onClick={onReset}>alle löschen</button>
                </div>
            )}
        </>
    );
}
